import {
  DvGridComponent,
  DvQuickFilterComponent,
  DvGridToolbarComponent,
  DvActionComponent,
} from '@devinco/ui/grid';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { pendingChangesGuard } from '@core/guards/pending-changes.guard';
import { userRolesAndPermissionsGuard } from '@core/guards/user-roles-and-permissions.guard';
import {
  DvDynamicFormModule,
  DvFilterAreaModule,
  DvNavTabsModule,
  DvSavePanelModule,
} from '@devinco/ui/legacy';
import { TripTypeSetupGridComponent } from '@features/trip-type-setup/trip-type-setup-grid/trip-type-setup-grid.component';
import { TripTypeSetupHeaderComponent } from '@features/trip-type-setup/trip-type-setup-header/trip-type-setup-header.component';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateModule } from '@ngx-translate/core';
import { VehicleLeasingTabComponent } from './components/vehicle-tabs/vehicle-leasing-tab/vehicle-leasing-tab.component';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { DirectivesModule } from '@shared/directives/directives.module';
import { MapModule } from '@features/map/map.module';
import { PipesModule } from '@shared/pipes/pipes.module';
import { AgGridModule } from 'ag-grid-angular';
import { NgxEchartsModule } from 'ngx-echarts';
import { VehicleInformationTabComponent } from './components/vehicle-tabs/vehicle-information-tab/vehicle-information-tab.component';
import { MileageHistoryGridComponent } from './components/vehicle-tabs/vehicle-mileage-history-tab/mileage-history-grid/mileage-history-grid.component';
import { VehicleMileageHistoryTabComponent } from './components/vehicle-tabs/vehicle-mileage-history-tab/vehicle-mileage-history-tab.component';
import { VehicleMileageTabComponent } from './components/vehicle-tabs/vehicle-mileage-tab/vehicle-mileage-tab.component';
import { VehiclePictureDocumentTabComponent } from './components/vehicle-tabs/vehicle-picture-document-tab/vehicle-picture-document-tab.component';
import { VehicleServiceTabComponent } from './components/vehicle-tabs/vehicle-service-tab/vehicle-service-tab.component';
import { VehicleTabsComponent } from './components/vehicle-tabs/vehicle-tabs.component';
import { VehicleTripTypeSetupTabComponent } from './components/vehicle-tabs/vehicle-trip-type-setup-tab/vehicle-trip-type-setup-tab.component';
import { VehiclesGridComponent } from './components/vehicles-grid/vehicles-grid.component';
import { VehiclesPageComponent } from './vehicles-page.component';
import { DvButtonComponent, DvTooltipDirective } from '@devinco/ui/dds';
import { VehiclePropertiesTabComponent } from './components/vehicle-tabs/vehicle-properties-tab/vehicle-properties-tab.component';
import { VehicleSetupTabComponent } from './components/vehicle-tabs/vehicle-setup-tab/vehicle-setup-tab.component';

import { DvVerticalSplitterComponent } from '@devinco/ui/dds/splitter';

export const VehicleRouteName = {
  Main: 'vehicles',
  Information: 'information',
  Mileage: 'mileage',
  MileageHistory: 'mileage-history',
  Service: 'service',
  TripSetup: 'trip-setup',
  Leasing: 'leasing',
  PictureDocument: 'picture-document',
  Properties: 'properties',
  VehicleSetup: 'setup',
};

const routes: Routes = [
  {
    path: '',
    component: VehiclesPageComponent,
    children: [
      {
        path: ':id',
        pathMatch: 'full',
        redirectTo: `:id/${VehicleRouteName.Information}`,
      },
      {
        path: ':id',
        component: VehicleTabsComponent,
        children: [
          {
            path: VehicleRouteName.Information,
            component: VehicleInformationTabComponent,
            canDeactivate: [
              (component: VehicleInformationTabComponent) =>
                pendingChangesGuard(component),
            ],
          },
          {
            path: VehicleRouteName.Mileage,
            component: VehicleMileageTabComponent,
            canDeactivate: [
              (component: VehicleMileageTabComponent) =>
                pendingChangesGuard(component),
            ],
            canActivate: [
              () =>
                userRolesAndPermissionsGuard([
                  RolesAndPermissions.VehicleAdjustMileageRead,
                ]),
            ],
          },
          {
            path: VehicleRouteName.MileageHistory,
            component: VehicleMileageHistoryTabComponent,
            canActivate: [
              () =>
                userRolesAndPermissionsGuard([
                  RolesAndPermissions.VehicleAdjustMileageRead,
                ]),
            ],
          },
          {
            path: VehicleRouteName.Service,
            component: VehicleServiceTabComponent,
            canDeactivate: [
              (component: VehicleServiceTabComponent) =>
                pendingChangesGuard(component),
            ],
          },
          {
            path: VehicleRouteName.TripSetup,
            component: VehicleTripTypeSetupTabComponent,
            canDeactivate: [
              (component: VehicleTripTypeSetupTabComponent) =>
                pendingChangesGuard(component),
            ],
            canActivate: [
              () =>
                userRolesAndPermissionsGuard(
                  [
                    RolesAndPermissions.TripSettingsTypeSetupRead,
                    RolesAndPermissions.VehicleEditMyPrivate,
                  ],
                  undefined,
                  false
                ),
            ],
          },
          {
            path: VehicleRouteName.VehicleSetup,
            component: VehicleSetupTabComponent,
            canDeactivate: [
              (component: VehicleSetupTabComponent) =>
                pendingChangesGuard(component),
            ],
            canActivate: [
              () =>
                userRolesAndPermissionsGuard(
                  [
                    RolesAndPermissions.TripSettingsTypeSetupRead,
                    RolesAndPermissions.VehicleEditMyPrivate,
                  ],
                  undefined,
                  false
                ),
            ],
          },
          {
            path: VehicleRouteName.Leasing,
            component: VehicleLeasingTabComponent,
            canDeactivate: [
              (component: VehicleLeasingTabComponent) =>
                pendingChangesGuard(component),
            ],
          },
          {
            path: VehicleRouteName.PictureDocument,
            component: VehiclePictureDocumentTabComponent,
          },
          {
            path: VehicleRouteName.Properties,
            component: VehiclePropertiesTabComponent,
          },
        ].filter((child: Route | undefined) => child) as Route[],
      },
    ],
  },
];

@NgModule({
  declarations: [
    VehiclesPageComponent,
    VehiclesGridComponent,
    VehicleInformationTabComponent,
    VehicleMileageTabComponent,
    VehicleServiceTabComponent,
    VehicleMileageHistoryTabComponent,
    MileageHistoryGridComponent,
    VehicleTripTypeSetupTabComponent,
    VehiclePictureDocumentTabComponent,
    VehicleLeasingTabComponent,
  ],
  imports: [
    DvFilterAreaModule,
    DvTooltipDirective,
    VehicleTabsComponent,
    AgGridModule,
    DvGridComponent,
    DvQuickFilterComponent,
    DvGridToolbarComponent,
    DvActionComponent,
    DirectivesModule,
    CommonModule,
    DvButtonComponent,
    DvDynamicFormModule,
    DvNavTabsModule,
    DvSavePanelModule,
    PipesModule,
    RouterModule.forChild(routes),
    TranslateModule,
    MapModule,
    DvVerticalSplitterComponent,
    TripTypeSetupGridComponent,
    TripTypeSetupHeaderComponent,
    TimedMarkerMapMenuPanelComponent,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
  ],
})
export class VehiclesPageModule {}
